import { useCallback, useMemo, useState } from "react";

import type { APILibraryTemplateResponseType } from "@/core/data_sources/models/APILibraryTemplate";
import { AppStore } from "@/core/data_sources/store/app";
import { Button } from "@/core/ui/atoms";
import {
  adminInputClasses,
  adminSelectClasses,
  AdminTableToolbar,
  Banner,
  DataTable,
  PageHeader,
} from "@/core/ui/molecules";
import { DialogTemplate } from "@/core/ui/templates";
import { useStore } from "@/core/utils/hooks";

import { ChipList, StatusBadge, TypeBadge } from "../../components/molecules";
import { ImportTemplateDialog } from "../review-queue/components/ImportTemplateDialog";
import { EditTemplateMetadataDialog } from "./components/EditTemplateMetadataDialog";
import { TemplateDetailDrawer } from "./components/TemplateDetailDrawer";
import { LibraryTemplatesStore } from "./data_sources/store";

type TemplateTypeFilter = "all" | "form" | "media" | "model";
type TemplateStatusFilter = "all" | "archived" | "draft" | "published";

export function TemplateCatalogPage() {
  const { actions, state, hooks } = useStore(LibraryTemplatesStore.instance);
  const { actions: appActions } = useStore(AppStore.instance);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TemplateTypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<TemplateStatusFilter>("all");
  const [verticalFilter, setVerticalFilter] = useState("all");

  const { setQuery } = hooks.useTemplateStoreService({
    page: 1,
    page_size: 10,
  });

  const closeDrawer = useCallback(() => {
    appActions.rightDrawer("close");
  }, [appActions]);
  const closeDialog = useCallback(() => {
    appActions.dialog("close");
  }, [appActions]);
  const refresh = useCallback(() => {
    setQuery((prev) => ({ ...prev }));
  }, [setQuery]);
  const handleSaved = useCallback(() => {
    refresh();
    closeDialog();
    closeDrawer();
  }, [closeDialog, closeDrawer, refresh]);
  const openImportTemplateDialog = useCallback(() => {
    appActions.dialog(
      "open",
      <DialogTemplate className="max-w-none">
        <ImportTemplateDialog onClose={closeDialog} onImport={refresh} />
      </DialogTemplate>,
    );
  }, [appActions, closeDialog, refresh]);
  const templates = useMemo(
    () => state?.list.map((template) => template.getData()) ?? [],
    [state?.list],
  );
  const verticals = useMemo(
    () =>
      Array.from(
        new Set(
          templates.flatMap((template) =>
            (template.verticals ?? []).map(
              (vertical) => vertical.name ?? vertical.code ?? "",
            ),
          ),
        ),
      ).filter(Boolean),
    [templates],
  );
  const filteredTemplates = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return templates.filter((template) => {
      const haystack = [
        template.name,
        template.description,
        template.type,
        template.status,
        template.version,
        ...(template.verticals ?? []).map((vertical) => vertical.name),
        ...(template.categories ?? []).map((category) => category.name),
      ]
        .join(" ")
        .toLowerCase();

      return (
        (!normalizedSearch || haystack.includes(normalizedSearch)) &&
        (typeFilter === "all" || template.type === typeFilter) &&
        (statusFilter === "all" || template.status === statusFilter) &&
        (verticalFilter === "all" ||
          template.verticals?.some(
            (vertical) => vertical.name === verticalFilter,
          ))
      );
    });
  }, [search, statusFilter, templates, typeFilter, verticalFilter]);
  const handlePublishTemplate = useCallback(
    (templateId: number) => actions.publishTemplate(templateId),
    [actions],
  );
  const handleArchiveTemplate = useCallback(
    (templateId: number) => actions.archiveTemplate(templateId),
    [actions],
  );
  const openEditTemplateMetadataDialog = useCallback(
    (template: APILibraryTemplateResponseType) => {
      appActions.dialog(
        "open",
        <DialogTemplate>
          <EditTemplateMetadataDialog
            onCancel={closeDialog}
            onSaved={handleSaved}
            template={template}
          />
        </DialogTemplate>,
      );
    },
    [appActions, closeDialog, handleSaved],
  );
  const openTemplateDrawer = useCallback(
    (template: APILibraryTemplateResponseType) => {
      appActions.rightDrawer(
        "open",
        620,
        <TemplateDetailDrawer
          onActionSuccess={handleSaved}
          onArchive={handleArchiveTemplate}
          onClose={closeDrawer}
          onEditMetadata={openEditTemplateMetadataDialog}
          onPublish={handlePublishTemplate}
          template={template}
          templates={templates}
        />,
      );
    },
    [
      appActions,
      closeDrawer,
      handleArchiveTemplate,
      handleSaved,
      handlePublishTemplate,
      openEditTemplateMetadataDialog,
      templates,
    ],
  );
  const columns = useMemo(
    () => [
      {
        header: "Template",
        key: "template",
        render: (template: APILibraryTemplateResponseType) => (
          <button
            className="max-w-xl text-left"
            onClick={() => openTemplateDrawer(template)}
            type="button"
          >
            <div className="font-extrabold text-ink">{template.name}</div>
            <div className="text-xs text-ink-muted">
              {template.description} · v{template.version ?? "-"}
            </div>
          </button>
        ),
      },
      {
        header: "Type",
        key: "type",
        render: (template: APILibraryTemplateResponseType) => (
          <TypeBadge type={template.type} />
        ),
      },
      {
        header: "Verticals",
        key: "verticals",
        render: (template: APILibraryTemplateResponseType) => (
          <ChipList
            values={template.verticals?.map(
              (vertical) => vertical.name ?? vertical.code,
            )}
          />
        ),
      },
      {
        header: "Category",
        key: "category",
        render: (template: APILibraryTemplateResponseType) =>
          template.categories?.[0]?.name ?? "-",
      },
      {
        header: "Status",
        key: "status",
        render: (template: APILibraryTemplateResponseType) => (
          <StatusBadge status={template.status} />
        ),
      },
      {
        header: "Used by Packs",
        key: "packs",
        render: (template: APILibraryTemplateResponseType) => (
          <ChipList values={template.packs} />
        ),
      },
      {
        className: "w-20",
        header: "",
        key: "action",
        render: (template: APILibraryTemplateResponseType) => (
          <Button onClick={() => openTemplateDrawer(template)} size="sm">
            Open
          </Button>
        ),
      },
    ],
    [openTemplateDrawer],
  );

  return (
    <>
      <PageHeader
        actions={
          <Button onClick={openImportTemplateDialog} variant="primary">
            Import Template JSON
          </Button>
        }
        breadcrumb={["Library", "Templates"]}
        subtitle="Published global catalog. Import JSON drafts here and use published templates through Template Packs."
        title="Templates"
      />
      <Banner>
        <strong>Simple rule:</strong> Templates shown here are already
        published. New JSON imports create draft templates. Add published
        templates into Template Packs, then allow packs in Plan Editor.
      </Banner>
      <DataTable
        columns={columns}
        header={
          <AdminTableToolbar>
            <input
              className={`${adminInputClasses} w-80 max-w-full`}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search templates..."
              value={search}
            />
            <div className="flex flex-wrap items-center gap-2">
              <select
                className={adminSelectClasses}
                onChange={(event) =>
                  setTypeFilter(event.target.value as TemplateTypeFilter)
                }
                value={typeFilter}
              >
                <option value="all">All types</option>
                <option value="form">Form</option>
                <option value="media">Media</option>
                <option value="model">Model</option>
              </select>
              <select
                className={adminSelectClasses}
                onChange={(event) =>
                  setStatusFilter(event.target.value as TemplateStatusFilter)
                }
                value={statusFilter}
              >
                <option value="all">All status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
              <select
                className={adminSelectClasses}
                onChange={(event) => setVerticalFilter(event.target.value)}
                value={verticalFilter}
              >
                <option value="all">All verticals</option>
                {verticals.map((vertical) => (
                  <option key={vertical} value={vertical}>
                    {vertical}
                  </option>
                ))}
              </select>
            </div>
          </AdminTableToolbar>
        }
        rowKey={(template) =>
          String(template.id ?? template.url ?? template.name ?? "")
        }
        rows={filteredTemplates}
      />
    </>
  );
}
